<script lang="ts" setup>
import { MenuButton, MenuItems, Menu as UiMenu } from '@headlessui/vue';
import type { PropType } from 'vue';
import { computed } from 'vue';

import {
  C4ButtonBase,
  type IconNameType,
  type SizeType,
  type ToneType,
  type VariationType
} from '@c4-links/vue3-links-components';

const props = defineProps({
  icon: {
    type: String as PropType<IconNameType>,
    required: false,
    default: undefined
  },
  position: {
    type: String as PropType<'left' | 'right'>,
    required: false,
    default: 'right'
  },
  variation: {
    type: String as PropType<VariationType>,
    default: 'tertiary' as VariationType
  },
  rounded: { type: Boolean, required: false, default: true },
  fullWidth: { type: Boolean, default: false },
  testId: { type: String, required: false, default: undefined },
  disabled: { type: Boolean, required: false, default: false },
  popover: { type: String, required: false, default: undefined },
  tone: {
    type: String as PropType<ToneType>,
    required: false,
    default: 'monochrome' as ToneType
  },
  size: {
    type: String as PropType<SizeType>,
    required: false,
    default: 'small' as SizeType
  }
});

const classes = computed(() => ({
  'w-full! max-w-full!': props.fullWidth
}));
</script>

<template>
  <UiMenu as="div" class="relative inline-block text-left" :class="classes" :data-testid="testId">
    <MenuButton v-if="$slots.default" class="flex items-center gap-x-2" :class="classes" :disabled="disabled">
      <span class="sr-only">Open menu</span>
      <C4ButtonBase
        :icon-left="icon"
        icon-right="chevron-down"
        :variation="variation"
        :full-width="fullWidth"
        :disabled="disabled"
        :tone="tone"
      >
        <slot />
      </C4ButtonBase>
    </MenuButton>

    <MenuButton v-if="!$slots.default" class="flex items-center rounded-full" :disabled="disabled">
      <span class="sr-only">Open menu</span>
      <C4ButtonBase
        :icon-left="icon || 'chevron-down'"
        :variation="variation"
        :full-width="fullWidth"
        :disabled="disabled"
        :tone="tone"
      />
    </MenuButton>
    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <MenuItems
        :class="[
          'absolute z-10 w-full min-w-fit max-w-xs rounded-md bg-white shadow-lg ring-black ring-opacity-5 focus:outline-none',
          {
            'right-0 origin-top-right': position === 'left',
            'left-0 origin-top-left': position === 'right'
          },
          classes
        ]"
      >
        <div class="py-1" :class="classes">
          <slot name="items" />
        </div>
      </MenuItems>
    </Transition>
  </UiMenu>
</template>
